import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OTPInput from "otp-input-react";
import NavBarAuth from "../../../../components/navbar/navbarAuth";
import AlertContext from "../../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../../Files/BaseURL";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ResetPassword = () => {
  const [OTP, setOTP] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [formErrors, setFormErrors] = useState({
    password: "",
    confirmPassword: "",
  });
  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from location state or session storage
  const email = location.state?.email || sessionStorage.getItem("forgot_email");

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (password.length > 30) {
      errors.push("Password must not exceed 30 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    if (!/[!@#$%^&*]/.test(password)) {
      errors.push("Password must contain at least one special character (!@#$%^&*)");
    }
    return errors;
  };

  const checkPasswordRequirement = (password, requirement) => {
    switch (requirement) {
      case "At least 8 characters long":
        return password.length >= 8;
      case "Maximum 30 characters":
        return password.length <= 30;
      case "At least one uppercase letter":
        return /[A-Z]/.test(password);
      case "At least one lowercase letter":
        return /[a-z]/.test(password);
      case "At least one number":
        return /[0-9]/.test(password);
      case "At least one special character (!@#$%^&*)":
        return /[!@#$%^&*]/.test(password);
      default:
        return false;
    }
  };

  const passwordRequirements = [
    "At least 8 characters long",
    "Maximum 30 characters",
    "At least one uppercase letter",
    "At least one lowercase letter",
    "At least one number",
    "At least one special character (!@#$%^&*)",
    "Passwords must match"
  ];

  const validateForm = () => {
    let isValid = true;
    const errors = {
      password: "",
      confirmPassword: "",
    };

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors[0];
      isValid = false;
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const handleVerifyOTP = async () => {
    if (!OTP || OTP.length !== 6) {
      showAlert("Please enter a valid 6-digit OTP", "danger");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${BaseURL}/api/authlogin/verify-and-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            otp: OTP,
            newPassword: password,
            token: sessionStorage.getItem("forgot_token"),
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        showAlert("Password reset successful!", "success");
        sessionStorage.removeItem("forgot_email");
        sessionStorage.removeItem("forgot_token");
        navigate("/signin");
      } else {
        showAlert(data.message || "Invalid OTP", "danger");
      }
    } catch (error) {
      console.error("Error:", error);
      showAlert("Error resetting password", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || !email) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${BaseURL}/api/authlogin/resend-forgot-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (data.success) {
        if (data.token) {
          sessionStorage.setItem("forgot_token", data.token);
        }
        setOTP("");
        showAlert("New OTP has been sent to your email", "success");
        setCanResend(false);
        setCountdown(30);
      } else {
        showAlert(data.message || "Failed to resend OTP", "danger");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      showAlert("Unable to resend OTP. Please try again later.", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavBarAuth />
      <div className="flex justify-center items-center bg-gray-100 py-20">
        <div className="mx-6 bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl text-center font-bold mb-4 w-[70%] md:w-[70%] lg:w-[50%]">
            Reset Password
          </h2>
          <p className="text-gray-600 text-sm font-pop mb-6 text-center max-w-sm">
            Enter the OTP sent to your email and your new password
          </p>

          <div className="mb-6">
            <div className="w-full px-6 border-2 border-transparent flex items-center justify-center font-pop text-lg rounded-full bg-[#E8E8E8] py-2">
              <OTPInput
                value={OTP}
                onChange={setOTP}
                autoFocus
                OTPLength={6}
                otpType="number"
                disabled={loading}
                secure
              />
            </div>
          </div>

          <div className="mb-4 relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFormErrors((prev) => ({ ...prev, password: "" }));
              }}
              placeholder="New Password"
              className="w-full px-6 py-2 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] focus:border-primarygreen focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
            </button>
            {formErrors.password && (
              <p className="text-red-500 text-sm mt-1 ml-4">
                {formErrors.password}
              </p>
            )}
          </div>

          <div className="mb-4 relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setFormErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }}
              placeholder="Confirm Password"
              className="w-full px-6 py-2 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] focus:border-primarygreen focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
            </button>
            {formErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1 ml-4">
                {formErrors.confirmPassword}
              </p>
            )}
          </div>

          <div className="mb-4 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold mb-2">Password Requirements:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              {passwordRequirements.map((requirement, index) => (
                <li key={index} className="flex items-center">
                  <span className={`mr-2 ${
                    requirement === "Passwords must match" 
                      ? (password && confirmPassword && password === confirmPassword ? 'text-green-500' : 'text-gray-400')
                      : (checkPasswordRequirement(password, requirement) ? 'text-green-500' : 'text-gray-400')
                  }`}>
                    {requirement === "Passwords must match"
                      ? (password && confirmPassword && password === confirmPassword ? '✓' : '○')
                      : (checkPasswordRequirement(password, requirement) ? '✓' : '○')}
                  </span>
                  {requirement}
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={handleVerifyOTP}
            className="w-full bg-green-500 hover:bg-transparent hover:bg-white-500 font-semibold hover:text-black font-pop text-xl py-3 px-4 border border-black-500 hover:border-green-500 rounded-full text-white animate__animated animate__backInUp"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              "Verify"
            )}
          </button>

          <div className="mt-6 text-center">
            <p className="text-gray-400 font-pop">
              Didn't receive the code?
            </p>
            {canResend ? (
              <button
                className="text-primarygreen cursor-pointer hover:underline font-pop"
                onClick={handleResendOTP}
                disabled={loading}
              >
                Resend
              </button>
            ) : (
              <p className="text-gray-500 font-pop">
                Resend available in {countdown} seconds
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
